import * as React from "react";
import { toast as sonner } from "sonner";

// Thin adapter over sonner that keeps the legacy shadcn toast call shape —
// `toast({ title, description, variant })` / `useToast()` — so the ~45 call
// sites (and lib/toast's toastError) stay untouched while the deprecated
// ui/toast + ui/toaster (radix) are gone. The renderer is ui/sonner's
// <Toaster />, mounted once in App.tsx.

type ToastInput = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** `destructive` renders as an error toast; anything else is a plain one. */
  variant?: "default" | "destructive";
  /** Milliseconds before auto-dismiss (sonner default: 4000). */
  duration?: number;
};

function toast({ title, description, variant, duration }: ToastInput) {
  // sonner takes the title as the message; a description-only call still
  // needs a message, so fall back to the description as the message.
  const message = title ?? description ?? "";
  const options = {
    description: title ? description : undefined,
    duration,
  };
  const id =
    variant === "destructive"
      ? sonner.error(message, options)
      : sonner(message, options);

  return {
    id,
    dismiss: () => sonner.dismiss(id),
    update: (props: ToastInput) => {
      const next = props.title ?? props.description ?? message;
      const nextOptions = {
        id,
        description: props.title ? props.description : undefined,
        duration: props.duration,
      };
      if (props.variant === "destructive") sonner.error(next, nextOptions);
      else sonner(next, nextOptions);
    },
  };
}

function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) => sonner.dismiss(toastId),
  };
}

export { toast, useToast };
